/**
 * FlowAction tool processor for the Now Assist Skill Plugin.
 * Handles processing of FlowAction tools defined in NowAssistSkillConfig.
 */

import {
    TOOL_TYPES,
    RESOURCE_ATTRIBUTE_VALUE_SCRIPT,
    RESOURCE_MAPPING_APPLICABILITY_SCRIPT,
    SYS_ONE_EXTEND_CAPABILITY_DEFINITION_API_TYPES,
} from '../defaults'
import type { Context, ObjectShape, Record as BuildRecord } from '@servicenow/sdk-build-core'
import { applyDefaults, buildDependsOnList, createDependencyEdges } from '../skill-plugin-helpers'
import type { ToolDefinition } from '../tool-types'
import {
    SYS_ONE_EXTEND_RESOURCE_MAPPING,
    SYS_ONE_EXTEND_RESOURCE_ATTRIBUTE_MAPPING,
    SYS_ONE_EXTEND_DEFINITION_ATTRIBUTE,
    normalizeInputFieldName,
    generateInputAttributeValueScript,
    validateToolDataType,
} from '../tool-utils'
import { createToolCapabilityInfra } from './tool-capability-helpers'

/**
 * Process a FlowAction tool and create the necessary records
 *
 * FlowAction tools create:
 * 1. Tool capability infrastructure (capability, definition, config, feature/strategy mappings)
 * 2. sys_one_extend_definition_attribute - Input definition attributes (with data_type)
 * 3. sys_one_extend_definition_attribute - Output definition attributes (with data_type)
 * 4. sys_one_extend_resource_mapping - The main tool record
 * 5. sys_one_extend_resource_attribute_mapping - Input attribute mappings (with type)
 * 6. sys_one_extend_resource_attribute_mapping - Output attribute mappings (with truncate)
 * 7. sys_one_extend_resource_edge - Dependency edges (if depends is specified)
 */
export async function processFlowActionTool(
    tool: ToolDefinition,
    capabilityRecord: BuildRecord,
    toolRecordsMap: Map<string, BuildRecord>,
    arg: ObjectShape,
    factory: Context['factory'],
    diagnostics: Context['diagnostics'],
    decisionToolNames?: Set<string>
): Promise<BuildRecord[]> {
    const records: BuildRecord[] = []

    if (tool.type !== TOOL_TYPES.FLOW_ACTION) {
        diagnostics.error(arg, `Expected flow-action tool but got ${tool.type}`)
        return records
    }

    if (!tool.name) {
        diagnostics.error(arg, 'FlowAction tool requires a name')
        return records
    }

    if (!tool.actionId) {
        diagnostics.error(arg, 'FlowAction tool requires an actionId')
        return records
    }

    const dependsOnList = buildDependsOnList(tool, toolRecordsMap, arg, diagnostics, decisionToolNames)
    const capabilityName = `${tool.actionId}_${tool.name}`

    // Create capability infrastructure records
    const infraResult = await createToolCapabilityInfra(
        tool,
        capabilityRecord,
        capabilityName,
        {
            name: capabilityName,
            api: tool.actionId,
            apiType: SYS_ONE_EXTEND_CAPABILITY_DEFINITION_API_TYPES.SYS_HUB_ACTION_TYPE_DEFINITION,
        },
        arg,
        factory
    )
    records.push(...infraResult.records)
    const { toolCapabilityRecord, toolCapabilityExplicitId } = infraResult

    // Create sys_one_extend_definition_attribute records for inputs
    // Store records in a map keyed by input name so we can reference them in attribute mappings
    const inputDefAttrRecordsMap = new Map<string, BuildRecord>()
    if (tool.inputs) {
        for (const input of tool.inputs) {
            // Validate input type for FlowAction tool
            const inputTypeError = validateToolDataType(input.type, input.name, tool.name, true)
            if (inputTypeError) {
                diagnostics.error(arg, inputTypeError)
                return records
            }

            const internalName = normalizeInputFieldName(input.name)
            const inputDataType = input.type || 'string'
            const inputDefAttrRecord = await factory.createRecord({
                source: tool._source!,
                table: SYS_ONE_EXTEND_DEFINITION_ATTRIBUTE,
                explicitId: input.$id,
                properties: arg.transform(({ $ }) => ({
                    ...applyDefaults(SYS_ONE_EXTEND_DEFINITION_ATTRIBUTE, $),
                    capability: $.val(toolCapabilityRecord.getId()),
                    name: $.val(internalName),
                    label: $.val(input.name),
                    type: $.val('input'),
                    data_type: $.val(inputDataType),
                })),
            })
            records.push(inputDefAttrRecord)
            inputDefAttrRecordsMap.set(input.name, inputDefAttrRecord)
        }
    }

    // Create sys_one_extend_definition_attribute records for outputs
    // Store records in a map keyed by output name so we can reference them in attribute mappings
    const outputDefAttrRecordsMap = new Map<string, BuildRecord>()
    if (tool.outputs && tool.outputs.length > 0) {
        for (const output of tool.outputs) {
            // Validate output type for FlowAction tool
            const outputTypeError = validateToolDataType(output.type, output.name, tool.name, false)
            if (outputTypeError) {
                diagnostics.error(arg, outputTypeError)
                return records
            }

            const internalName = normalizeInputFieldName(output.name)
            const outputDataType = output.type || 'string'
            const outputDefAttrRecord = await factory.createRecord({
                source: tool._source!,
                table: SYS_ONE_EXTEND_DEFINITION_ATTRIBUTE,
                explicitId: output.$id,
                properties: arg.transform(({ $ }) => ({
                    ...applyDefaults(SYS_ONE_EXTEND_DEFINITION_ATTRIBUTE, $),
                    capability: $.val(toolCapabilityRecord.getId()),
                    name: $.val(internalName),
                    label: $.val(output.name),
                    type: $.val('output'),
                    data_type: $.val(outputDataType),
                })),
            })
            records.push(outputDefAttrRecord)
            outputDefAttrRecordsMap.set(output.name, outputDefAttrRecord)
        }
    }

    // Build metadata with actionId and toolType for reverse detection
    const metadata: Record<string, unknown> = {
        toolType: TOOL_TYPES.FLOW_ACTION,
        actionId: tool.actionId,
    }

    // Create sys_one_extend_resource_mapping record
    // resource_capability points to the tool's capability record, NOT the actionId
    const resourceMappingRecord = await factory.createRecord({
        source: tool._source!,
        table: SYS_ONE_EXTEND_RESOURCE_MAPPING,
        ...(tool.$id ? { explicitId: tool.$id } : {}),
        properties: arg.transform(({ $ }) => ({
            ...applyDefaults(SYS_ONE_EXTEND_RESOURCE_MAPPING, $),
            applicability_script: $.val(RESOURCE_MAPPING_APPLICABILITY_SCRIPT).toCdata(),
            depends_on_list: $.val(dependsOnList),
            metadata: $.val(JSON.stringify(metadata)),
            parent_capability: $.val(capabilityRecord.getId()),
            resource_capability: $.val(toolCapabilityRecord.getId()),
            resource_name: $.val(tool.name),
        })),
    })
    records.push(resourceMappingRecord)

    toolRecordsMap.set(tool.name, resourceMappingRecord)

    // Create sys_one_extend_resource_attribute_mapping for inputs (with type)
    if (tool.inputs) {
        for (const input of tool.inputs) {
            const isScripted = input.valueType === 'script'
            const inputScript = isScripted
                ? input.scriptValue!
                : generateInputAttributeValueScript(input.name, input.value)
            const inputDefAttrRecord = inputDefAttrRecordsMap.get(input.name)
            const inputAttributeRecord = await factory.createRecord({
                source: tool._source!,
                table: SYS_ONE_EXTEND_RESOURCE_ATTRIBUTE_MAPPING,
                properties: arg.transform(({ $ }) => ({
                    ...applyDefaults(SYS_ONE_EXTEND_RESOURCE_ATTRIBUTE_MAPPING, $),
                    attribute_value_script: $.val(inputScript).toCdata(),
                    attribute_value_template: $.val(input.value),
                    attribute_value_type: $.val(isScripted ? 'script' : 'template'),
                    resource_attribute_name: inputDefAttrRecord ? $.val(inputDefAttrRecord.getId()) : $.val(input.name),
                    resource_mapping: $.val(resourceMappingRecord.getId()),
                    type: $.val('input'),
                })),
            })
            records.push(inputAttributeRecord)
        }
    }

    // Create sys_one_extend_resource_attribute_mapping for outputs
    if (tool.outputs) {
        for (const output of tool.outputs) {
            const outputTruncate = output.truncate ?? false
            const outputDefAttrRecord = outputDefAttrRecordsMap.get(output.name)
            const outputAttributeRecord = await factory.createRecord({
                source: tool._source!,
                table: SYS_ONE_EXTEND_RESOURCE_ATTRIBUTE_MAPPING,
                properties: arg.transform(({ $ }) => ({
                    ...applyDefaults(SYS_ONE_EXTEND_RESOURCE_ATTRIBUTE_MAPPING, $),
                    attribute_value_script: $.val(RESOURCE_ATTRIBUTE_VALUE_SCRIPT).toCdata(),
                    resource_attribute_name: outputDefAttrRecord
                        ? $.val(outputDefAttrRecord.getId())
                        : $.val(output.name),
                    resource_mapping: $.val(resourceMappingRecord.getId()),
                    truncate: $.val(outputTruncate),
                    type: $.val('output'),
                })),
            })
            records.push(outputAttributeRecord)
        }
    }

    const edgeRecords = await createDependencyEdges(
        tool,
        toolRecordsMap,
        resourceMappingRecord,
        arg,
        factory,
        true,
        tool._source!
    )
    records.push(...edgeRecords)

    return records
}
